import React, { useState, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { ASSISTANT_AUTH_LOGIN_URL, ASSISTANT_AUTH_GOOGLE_URL, ASSISTANT_AUTH_MICROSOFT_URL } from '../constants';

/**
 * Login component handles user authentication.
 * Allows users to log in with email/password or via Google/Microsoft OAuth.
 * 
 * @returns {JSX.Element} - The rendered Login component.
 */
const Login = () => {
    const [formData, setFormData] = useState({ email: '', password: '' });
    const [error, setError] = useState(null);
    const { login } = useContext(AuthContext);
    const navigate = useNavigate();

    /**
     * Handles form input changes.
     * 
     * @param {Object} e - The event object.
     */
    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    /**
     * Handles form submission for email/password login.
     * 
     * @param {Object} e - The event object.
     */
    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);

        try {
            const res = await axios.post(ASSISTANT_AUTH_LOGIN_URL, formData, { withCredentials: true });

            if (res.data.token) {
                login(res.data.token); // Store token in context
                navigate('/dashboard'); // Redirect to dashboard
            } else {
                setError('Login failed. Please try again.');
            }
        } catch (err) {
            console.error('Request error:', err.response ? err.response.data : err.message);
            setError(err.response?.data?.msg || 'An error occurred. Please try again.');
        }
    };

    /**
     * Redirects to Google OAuth for authentication.
     */
    const handleGoogleSignIn = () => {
        window.open(ASSISTANT_AUTH_GOOGLE_URL, '_self');
    };

    /**
     * Redirects to Microsoft OAuth for authentication.
     */
    const handleMicrosoftSignIn = () => {
        window.open(ASSISTANT_AUTH_MICROSOFT_URL, '_self');
    };

    return (
        <div className="flex justify-center items-center h-screen bg-gray-100">
            <div className="w-full max-w-md p-8 bg-white shadow-md rounded-lg">
                <h2 className="text-2xl font-bold mb-6 text-center text-gray-900">Login</h2>
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <input
                            type="email"
                            name="email"
                            placeholder="Email"
                            value={formData.email}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Password</label>
                        <input
                            type="password"
                            name="password"
                            placeholder="Password"
                            value={formData.password}
                            onChange={handleChange}
                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                    {error && <p className="text-red-500 text-sm">{error}</p>}
                    <button
                        type="submit"
                        className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >
                        Login
                    </button>
                </form>

                <div className="mt-4">
                    <button
                        onClick={handleGoogleSignIn}
                        className="w-full bg-red-500 text-white py-2 px-4 rounded-md shadow-sm hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                        Sign in with Google
                    </button>
                </div>

                <div className="mt-4">
                    <button
                        onClick={handleMicrosoftSignIn}
                        className="w-full bg-blue-500 text-white py-2 px-4 rounded-md shadow-sm hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Sign in with Microsoft
                    </button>
                </div>

                <div className="mt-4 text-center">
                    <p className="text-sm text-gray-600">Don't have an account?
                        <Link to="/register" className="text-indigo-600 hover:underline"> Register</Link>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Login;
